<script setup lang="ts">
import { usePage } from '@inertiajs/vue3';
import { computed } from 'vue';

import { GlobalSearchDialog, GlobalSearchInput } from '@ja/crud/components';
import { useGlobalKeyboardShortcuts } from '@ja/crud/composables';
import OfflineBanner from '@ja/portal-query/components/OfflineBanner.vue';
import { useNetworkStatus } from '@ja/portal-query/composables/useNetworkStatus';

import BaseLayout from './BaseLayout.vue';

const page = usePage<{ canUseGlobalSearch?: boolean }>();

// Server tells us whether the current user can list any searchable CRUD
// model. Hide the search bar (and the dialog/shortcuts that drive it)
// when they can't — clicking through would only surface 403s.
const canSearch = computed(() => page.props.canUseGlobalSearch === true);

// Register global keyboard shortcuts for search (Cmd+K, /, Escape) only
// when search is actually available.
if (canSearch.value) {
    useGlobalKeyboardShortcuts();
}

const { isOnline } = useNetworkStatus();
</script>

<template>
    <BaseLayout>
        <!-- Inject global search input or offline banner into header -->
        <template #header-search>
            <OfflineBanner v-if="!isOnline" :is-online="isOnline" dismissable />
            <GlobalSearchInput v-else-if="canSearch" />
        </template>

        <!-- Pass through the default slot (page content) -->
        <slot />

        <!-- Inject global search dialog overlay -->
        <template #overlays>
            <GlobalSearchDialog v-if="canSearch" />
        </template>
    </BaseLayout>
</template>
